import { useRef, useState } from "react";
import { Element } from "react-scroll";

export type FaqItem = { question: string; answer: string };

const FaqRow = ({
  item,
  isOpen,
  onClick,
  elementName,
}: {
  item: FaqItem;
  isOpen: boolean;
  onClick: () => void;
  elementName?: string;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const row = (
    <div className="border-t border-brown-100 last:border-b">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={onClick}
        className="w-full flex items-center gap-4 py-4 text-left [-webkit-tap-highlight-color:transparent] focus:outline-none"
      >
        <span
          className={`flex-none w-6 h-6 rounded-full bg-brown-100 text-brown-500 flex items-center justify-center font-bold transition-transform duration-300 ${
            isOpen ? "rotate-45" : ""
          }`}
        >
          +
        </span>
        <span className="font-semibold text-landing-blue">{item.question}</span>
      </button>
      <div
        ref={contentRef}
        style={{ maxHeight: isOpen ? contentRef.current?.scrollHeight ?? 600 : 0 }}
        className="overflow-hidden transition-all duration-500 ease-in-out"
      >
        <div
          className="pb-5 pl-10 text-sm text-landing-blue/80 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-2"
          dangerouslySetInnerHTML={{ __html: item.answer }}
        />
      </div>
    </div>
  );

  return elementName ? <Element name={elementName}>{row}</Element> : row;
};

const FaqAccordion = ({
  items,
  openIndex,
  setOpenIndex,
  elementPrefix,
}: {
  items: FaqItem[];
  openIndex?: number;
  setOpenIndex?: (idx: number) => void;
  elementPrefix?: string;
}) => {
  const [internalIndex, setInternalIndex] = useState(-1);
  const isControlled = openIndex !== undefined && setOpenIndex !== undefined;
  const activeIndex = isControlled ? openIndex : internalIndex;

  const handleClick = (idx: number) => {
    const next = activeIndex === idx ? -1 : idx;
    if (isControlled) setOpenIndex?.(next);
    else setInternalIndex(next);
  };

  return (
    <div className="mt-1">
      {items.map((item, idx) => (
        <FaqRow
          key={idx}
          item={item}
          isOpen={activeIndex === idx}
          onClick={() => handleClick(idx)}
          elementName={elementPrefix ? `${elementPrefix}-${idx}` : undefined}
        />
      ))}
    </div>
  );
};

export default FaqAccordion;
